import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { LogModule, LogService } from '../services/log.service';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ApolloError } from '@apollo/client/core';
import { ErrorDialogComponent } from '../shared/error-dialog/error-dialog.component';


@Injectable()
export class AppErrorHandler implements ErrorHandler {

  private lastShownAt = 0
  constructor(
    private logService: LogService,
    private injector: Injector,
    private zone: NgZone
  ) {}

  handleError(error: any): void {
    const dialog = this.injector.get(MatDialog);
    const {userMessage, details} = normalizeError(error);
    
    this.logService.logError(`[UI Error] ${userMessage}\n${details || ''}`, LogModule.UI);
    
    // In prod: show dialog, but don't spam (throttle to 1 per 4s)
    const now = Date.now();
    if (now - this.lastShownAt < 4000) return;
    this.lastShownAt = now;

    this.zone.run(() => {
      dialog.open(ErrorDialogComponent, {
        width: '520px',
        data: {
          title: 'Unexpected error',
          message: userMessage,
          // optionally hide details by default; toggle with a button
          details: details 
        },
      });
    });
  }
}

function normalizeError(err: any): { userMessage: string; details: string } {
  let userMessage = 'An unexpected error occurred.';
  let details = '';

  // HTTP
  if (err instanceof HttpErrorResponse) {
    const status = err.status;
    const msg = err.error?.message || err.message || 'Network error';
    return {
      userMessage:
        status >= 500
          ? 'The server is having trouble right now. Please try again shortly.'
          : status === 0
          ? 'Network is unreachable. Please check your connection.'
          : `Request failed (${status}).`,
      details: `HTTP ${status}\n${safeStringify(err.error) || msg}\n${err.url || ''}`,
    };
  } 

  // Apollo/GraphQL
  if (err instanceof ApolloError) {
    const graphqLErrs = err.graphQLErrors?.map(e => e.message).join('\n');
    return {
      userMessage: 'A data error occurred. Please try again.',
      details: `ApolloError: ${err.message}\n${graphqLErrs || ''}\n${err.networkError || ''}`,
    };
  }
  
  // DOM / Permissions / Media / SIP-ish
  const msg = String(err?.message || err);
  if (/NotAllowedError|Permission denied|device not found/i.test(msg)) {
    return {
      userMessage: 'Microphone is blocked or not available. Please allow mic access and try again.',
      details: msg,
    };
  }

  if (/play\(\) failed|AudioContext was not allowed/i.test(msg)) {
    return {
      userMessage: 'Audio couldn’t start because the page wasn’t interacted with. Click anywhere and retry.',
      details: msg,
    };
  }

   // Fallback
  return {
    userMessage: 'Something went wrong. Please try again.',
    details: (err && (err.stack || err.message)) ? `${err.message}\n${err.stack}` : safeStringify(err),
  };

  return { userMessage, details };
}

function safeStringify(v: any) {
  try { return typeof v === 'string' ? v : JSON.stringify(v, null, 2); } catch { return ''; }
}
